/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

export function CostLedgerInfo({ formData }) {
  const { values, setFieldValue, containView } = formData;
  const {
    itemsHeader = [],
    openingBalance = { totalAmount: 0, itemAmounts: [] },
    details = [],
    totalIncurred = { totalAmount: 0, itemAmounts: [] },
    creditAccount = { accountNumber: "", amount: 0 },
    closingBalance = { totalAmount: 0, itemAmounts: [] }
  } = values;

  const num = (v) => (v && v !== "" && !isNaN(v) ? parseFloat(v) : 0);
  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "0");

  const calcRowTotal = (itemAmounts) => {
    return (itemAmounts || []).reduce((acc, curr) => acc + num(curr), 0);
  };

  // Add/Remove Columns
  const addItemHeader = () => {
    const newHeaders = [...itemsHeader, "...."];
    setFieldValue("itemsHeader", newHeaders);

    const updateRow = (row) => ({
      ...row,
      itemAmounts: [...(row.itemAmounts || []), 0]
    });

    setFieldValue("openingBalance", updateRow(openingBalance));
    setFieldValue("details", details.map(d => ({
      ...d,
      amounts: updateRow(d.amounts || { totalAmount: 0, itemAmounts: [] })
    })));
  };

  const removeItemHeader = (idx) => {
    if (itemsHeader.length <= 1) return;
    const newHeaders = itemsHeader.filter((_, i) => i !== idx);
    setFieldValue("itemsHeader", newHeaders);

    const updateRow = (row) => ({
      ...row,
      itemAmounts: (row.itemAmounts || []).filter((_, i) => i !== idx)
    });

    setFieldValue("openingBalance", updateRow(openingBalance));
    setFieldValue("details", details.map(d => ({
      ...d,
      amounts: updateRow(d.amounts || { totalAmount: 0, itemAmounts: [] })
    })));
  };

  const handleHeaderChange = (idx, val) => {
    const newHeaders = [...itemsHeader];
    newHeaders[idx] = val;
    setFieldValue("itemsHeader", newHeaders);
  };

  // Add/Remove Rows (details)
  const addDetailRow = () => {
    const newRow = {
      bookingDate: "",
      documentNumber: "",
      documentDate: "",
      description: "",
      correspondingAccount: "",
      amounts: { totalAmount: 0, itemAmounts: Array(itemsHeader.length).fill(0) }
    };
    setFieldValue("details", [...details, newRow]);
  };

  const removeDetailRow = (idx) => {
    setFieldValue("details", details.filter((_, i) => i !== idx));
  };

  // Auto Calculations
  useEffect(() => {
    // 1. Calculate each row's totalAmount
    const newOpening = { ...openingBalance, totalAmount: calcRowTotal(openingBalance.itemAmounts) };

    let sumIncurredAmounts = Array(itemsHeader.length).fill(0);
    const newDetails = details.map(d => {
      const rowTotal = calcRowTotal(d.amounts?.itemAmounts);
      (d.amounts?.itemAmounts || []).forEach((val, i) => {
        if (i < sumIncurredAmounts.length) sumIncurredAmounts[i] += num(val);
      });
      return {
        ...d,
        amounts: { ...d.amounts, totalAmount: rowTotal }
      };
    });

    const totalIncurredVal = {
      totalAmount: sumIncurredAmounts.reduce((a, b) => a + b, 0),
      itemAmounts: sumIncurredAmounts
    };

    // 2. Calculate Closing Balance
    // Formula: Closing = Opening + TotalIncurred - Credit (Lưu ý: Credit thường kết chuyển 1 cục hoặc theo khoản mục)
    // Ở đây ta tính ClosingTotal = OpeningTotal + IncurredTotal - CreditAmount
    const closingTotal = num(newOpening.totalAmount) + num(totalIncurredVal.totalAmount) - num(creditAccount.amount);

    // Phân bổ Closing itemAmounts (Ví dụ: Opening + Incurred - 0, vì CreditAccount thường là kết chuyển tổng)
    const closingItemAmounts = newOpening.itemAmounts.map((val, i) => {
      return num(val) + num(totalIncurredVal.itemAmounts[i]);
    });

    const newClosing = {
      totalAmount: closingTotal,
      itemAmounts: closingItemAmounts
    };

    const hasChanged =
      JSON.stringify(newOpening) !== JSON.stringify(openingBalance) ||
      JSON.stringify(newDetails) !== JSON.stringify(details) ||
      JSON.stringify(totalIncurredVal) !== JSON.stringify(totalIncurred) ||
      JSON.stringify(newClosing) !== JSON.stringify(closingBalance);

    if (hasChanged) {
      setFieldValue("openingBalance", newOpening);
      setFieldValue("details", newDetails);
      setFieldValue("totalIncurred", totalIncurredVal);
      setFieldValue("closingBalance", newClosing);
    }
  }, [JSON.stringify(openingBalance.itemAmounts), JSON.stringify(details.map(d => d.amounts?.itemAmounts)), creditAccount.amount]);

  const s = {
    container: { fontFamily: '"Times New Roman", Times, serif', color: "#000", padding: "20px", backgroundColor: "#fff", fontSize: "14px" },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
    titleBox: { textAlign: "center", margin: "10px 0" },
    inputDotted: { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: "14px", fontFamily: "inherit", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    inputDottedSmall: { border: "none", borderBottom: "1px dotted #000", outline: "none", width: "40px", fontSize: "14px", fontFamily: "inherit", textAlign: "center", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "15px" },
    th: { border: "1px solid #000", padding: "8px 4px", textAlign: "center", fontWeight: "bold", verticalAlign: "middle", backgroundColor: "#f9f9f9" },
    td: { border: "1px solid #000", padding: 0, height: "35px", verticalAlign: "middle" },
    inputTable: { width: "100%", height: "100%", border: "none", outline: "none", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableCenter: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "center", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableRight: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "right", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    removeBtn: { border: "none", background: "transparent", color: "red", cursor: "pointer", fontWeight: "bold", fontSize: "16px" }
  };

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "60%" }}>
          <div>Đơn vị: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.unit} onChange={(e) => setFieldValue("unit", e.target.value)} /></div>
          <div>Địa chỉ: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.address} onChange={(e) => setFieldValue("address", e.target.value)} /></div>
        </div>
        <div style={{ textAlign: "center", width: "40%" }}>
          <div style={{ fontWeight: "bold" }}>Mẫu số S36-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "11px" }}>(Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={{ margin: 0, textTransform: "uppercase" }}>SỔ CHI PHÍ SẢN XUẤT, KINH DOANH</h2>
        <div style={{ fontStyle: "italic", fontSize: "12px", marginTop: "5px" }}>(Dùng cho các TK 621, 622, 623, 627, 154, 641, 642, 242, 335, 632)</div>

        <div style={{ marginTop: 15 }}>
          - Tài khoản: <input disabled={containView} style={{ ...s.inputDotted, width: "250px" }} value={values.accountNumber} onChange={(e) => setFieldValue("accountNumber", e.target.value)} />
        </div>
        <div style={{ marginTop: 5 }}>
          - Tên phân xưởng: <input disabled={containView} style={{ ...s.inputDotted, width: "250px" }} value={values.workshopName} onChange={(e) => setFieldValue("workshopName", e.target.value)} />
        </div>
        <div style={{ marginTop: 5 }}>
          - Tên sản phẩm, dịch vụ: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.productName} onChange={(e) => setFieldValue("productName", e.target.value)} />
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: "80px" }} rowSpan={2}>Ngày, tháng ghi sổ</th>
              <th style={s.th} colSpan={2}>Chứng từ</th>
              <th style={{ ...s.th, width: "200px" }} rowSpan={2}>Diễn giải</th>
              <th style={{ ...s.th, width: "60px" }} rowSpan={2}>TK đối ứng</th>
              <th style={s.th} colSpan={1 + itemsHeader.length}>Ghi Nợ Tài khoản...</th>
              {!containView && <th style={{ ...s.th, width: "40px" }} rowSpan={2}>#</th>}
            </tr>
            <tr>
              <th style={{ ...s.th, width: "60px" }}>Số hiệu</th>
              <th style={{ ...s.th, width: "60px" }}>Ngày tháng</th>
              <th style={{ ...s.th, width: "100px" }}>Tổng số tiền</th>
              {itemsHeader.map((header, idx) => (
                <th key={idx} style={{ ...s.th, width: "100px", position: "relative" }}>
                  <input disabled={containView} style={s.inputTableCenter} value={header} onChange={(e) => handleHeaderChange(idx, e.target.value)} />
                  {!containView && itemsHeader.length > 1 && (
                    <button type="button" style={{ ...s.removeBtn, position: "absolute", top: -8, right: 0 }} onClick={() => removeItemHeader(idx)}>×</button>
                  )}
                </th>
              ))}
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              {itemsHeader.map((_, idx) => (
                <th key={idx} style={s.th}>{idx + 2}</th>
              ))}
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {/* Opening Balance Row */}
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Số dư đầu kỳ</td>
              <td style={s.td}></td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(openingBalance.totalAmount)}</td>
              {(openingBalance.itemAmounts || []).map((val, iIdx) => (
                <td key={iIdx} style={s.td}>
                  <input
                    disabled={containView}
                    type="number"
                    style={s.inputTableRight}
                    value={val}
                    onChange={(e) => {
                      const newVal = num(e.target.value);
                      const newItemAmounts = [...openingBalance.itemAmounts];
                      newItemAmounts[iIdx] = newVal;
                      setFieldValue("openingBalance.itemAmounts", newItemAmounts);
                    }}
                  />
                </td>
              ))}
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* Details Rows */}
            <tr style={{ fontStyle: "italic" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Số phát sinh trong kỳ</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              {itemsHeader.map((_, i) => <td key={i} style={s.td}></td>)}
              {!containView && <td style={s.td}></td>}
            </tr>

            {(details || []).map((item, idx) => (
              <tr key={idx}>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.bookingDate} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].bookingDate = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentNumber} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].documentNumber = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentDate} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].documentDate = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTable} value={item.description} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].description = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.correspondingAccount} onChange={(e) => {
                  const newDetails = [...details];
                  newDetails[idx].correspondingAccount = e.target.value;
                  setFieldValue("details", newDetails);
                }} /></td>
                <td style={{ ...s.td, textAlign: "right", padding: "0 8px", fontWeight: "bold" }}>{fmt(item.amounts?.totalAmount)}</td>
                {(item.amounts?.itemAmounts || []).map((val, iIdx) => (
                  <td key={iIdx} style={s.td}>
                    <input
                      disabled={containView}
                      type="number"
                      style={s.inputTableRight}
                      value={val}
                      onChange={(e) => {
                        const newVal = num(e.target.value);
                        const newDetails = [...details];
                        const newItemAmounts = [...newDetails[idx].amounts.itemAmounts];
                        newItemAmounts[iIdx] = newVal;
                        newDetails[idx].amounts.itemAmounts = newItemAmounts;
                        setFieldValue("details", newDetails);
                      }}
                    />
                  </td>
                ))}
                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeDetailRow(idx)}>–</button>
                  </td>
                )}
              </tr>
            ))}

            {!containView && (
              <tr>
                <td colSpan={6 + itemsHeader.length} style={{ textAlign: "center", padding: "4px" }}>
                  <SoftBox mt={2} mb={2}>
                    <SoftButton variant="outlined" color="dark" size="small" onClick={addDetailRow}>+ Thêm dòng phát sinh</SoftButton>
                  </SoftBox>
                </td>
              </tr>
            )}

            {/* Total Incurred */}
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Cộng số phát sinh trong kỳ</td>
              <td style={s.td}></td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(totalIncurred.totalAmount)}</td>
              {totalIncurred.itemAmounts.map((val, i) => (
                <td key={i} style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(val)}</td>
              ))}
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* Credit Account Row */}
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>
                - Ghi Có TK: <input disabled={containView} style={{ ...s.inputDotted, width: "100px", textAlign: "center" }} value={creditAccount.accountNumber} onChange={(e) => setFieldValue("creditAccount.accountNumber", e.target.value)} />
              </td>
              <td style={s.td}></td>
              <td style={s.td}>
                <input
                  disabled={containView}
                  type="number"
                  style={{ ...s.inputTableRight, fontWeight: "bold" }}
                  value={creditAccount.amount}
                  onChange={(e) => setFieldValue("creditAccount.amount", e.target.value)}
                />
              </td>
              {itemsHeader.map((_, i) => <td key={i} style={{ ...s.td, textAlign: "center" }}>x</td>)}
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* Closing Balance Row */}
            <tr style={{ fontWeight: "bold", backgroundColor: "#fafafa" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Số dư cuối kỳ</td>
              <td style={s.td}></td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 8px", color: "blue" }}>{fmt(closingBalance.totalAmount)}</td>
              {closingBalance.itemAmounts.map((val, i) => (
                <td key={i} style={{ ...s.td, textAlign: "right", padding: "0 8px" }}>{fmt(val)}</td>
              ))}
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>

        {!containView && (
          <SoftBox mt={2}>
            <SoftButton variant="outlined" color="info" size="small" onClick={addItemHeader}>+ Thêm khoản mục (Cột)</SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: "25px" }}>
        <div style={{ marginBottom: "5px" }}>- Sổ này có <input disabled={containView} style={s.inputDottedSmall} value={values.totalPages} onChange={(e) => setFieldValue("totalPages", e.target.value)} /> trang, đánh số từ trang 01 đến trang...</div>
        <div>- Ngày mở sổ: <input disabled={containView} style={{ ...s.inputDotted, width: "150px" }} value={values.openingDate} onChange={(e) => setFieldValue("openingDate", e.target.value)} /></div>
      </div>

      {/* FOOTER SIGNATURES - Commented out as requested in similar modules, or will be added if needed */}
      {/* <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", textAlign: "center" }}>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Người ghi sổ</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.ledgerClerk} onChange={(e) => setFieldValue("ledgerClerk", e.target.value)} /></SoftBox>
        </div>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Kế toán trưởng</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.chiefAccountant} onChange={(e) => setFieldValue("chiefAccountant", e.target.value)} /></SoftBox>
        </div>
        <div style={{ width: "40%" }}>
          <div style={{ fontStyle: "italic", fontSize: "13px", marginBottom: "5px" }}>Ngày <input disabled={containView} style={s.inputDottedSmall} value={values.signingDay} onChange={(e) => setFieldValue("signingDay", e.target.value)} /> tháng <input disabled={containView} style={s.inputDottedSmall} value={values.signingMonth} onChange={(e) => setFieldValue("signingMonth", e.target.value)} /> năm <input disabled={containView} style={{ ...s.inputDottedSmall, width: "50px" }} value={values.signingYear} onChange={(e) => setFieldValue("signingYear", e.target.value)} /></div>
          <b style={{ textTransform: "none" }}>Người đại diện theo pháp luật</b><br/><span style={{ fontStyle: "italic", fontSize: "12px" }}>(Ký, họ tên, đóng dấu)</span>
          <SoftBox mt={8}><input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.legalRepresentative} onChange={(e) => setFieldValue("legalRepresentative", e.target.value)} /></SoftBox>
        </div>
      </div> */}
    </SoftBox>
  );
}
